import type { HttpClient } from '../http/HttpClient.ts';

interface Named {
  id: string;
  name: string;
}

/** An agent's owner: no name when they left every organization you share with them. */
interface Owner {
  id: string;
  name: string | null;
}

export type JournalKind =
  'plan' | 'note' | 'decision' | 'question' | 'blocker' | 'done' | 'handoff';

export interface JournalEntry {
  id: string;
  sessionId: string;
  agent: Named;
  owner: Owner;
  kind: JournalKind;
  text: string;
  items: { id: string; number: number; key: string; title: string }[];
  files: string[];
  areas: string[];
  private: boolean;
  handoff: { done: string[]; next: string[]; openQuestions: string[] } | null;
  createdAt: string;
}

export interface AgentSession {
  id: string;
  agent: Named;
  owner: Owner;
  goal: string;
  client: string;
  status: 'active' | 'idle' | 'ended';
  startedAt: string;
  lastSeenAt: string;
  endedAt: string | null;
  /** How it ended: a handoff, or idle for 2 hours ("ended without a handoff"). */
  endReason: 'handoff' | 'idle' | null;
  entryCount: number;
  handoff: JournalEntry | null;
}

/** An agent working on an item: "With Build bot (Olive's agent)". */
export interface ItemClaim {
  agent: Named;
  owner: Owner;
  sessionId: string | null;
  claimedAt: string;
}

export interface NoteQuery {
  q?: string;
  path?: string;
  area?: string;
  item?: string;
  agent?: string;
  owner?: string;
  session?: string;
  kind?: JournalKind;
  before?: string;
}

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}`;

/** What agents are doing in a project: sessions, journals, note search, claims. */
export class AgentWorkApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  sessions(
    org: string,
    key: string,
    query: { open?: boolean; before?: string } = {},
  ): Promise<{ sessions: AgentSession[] }> {
    const params = new URLSearchParams();
    if (query.open) params.set('open', 'true');
    if (query.before) params.set('before', query.before);
    const qs = params.toString();
    return this.#http.get(`${base(org, key)}/agent-sessions${qs ? `?${qs}` : ''}`);
  }

  session(
    org: string,
    key: string,
    id: string,
  ): Promise<{ session: AgentSession & { entries: JournalEntry[] } }> {
    return this.#http.get(`${base(org, key)}/agent-sessions/${encodeURIComponent(id)}`);
  }

  notes(org: string, key: string, query: NoteQuery): Promise<{ entries: JournalEntry[] }> {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(query))
      if (value !== undefined && value !== '') params.set(name, String(value));
    const qs = params.toString();
    return this.#http.get(`${base(org, key)}/agent-notes${qs ? `?${qs}` : ''}`);
  }

  /** A person clears an agent's claim on an item. */
  clearClaim(org: string, key: string, number: number): Promise<void> {
    return this.#http.delete(`${base(org, key)}/items/${number}/claim`);
  }
}
